import {hasModule} from '../ModuleUtil';
import {MODULE_TYPES} from '../../../../constants';

/**
 * These functions act as intermediate solutions for handling view configurations demanded by Sage until we can find a
 * good approach to unifying the view restrictions originating from Sage with the view restrictions originating from CompanySetup.
 */
function sageRestrictedFinancialModules(): {isPSAFullFinancials: boolean; isPSARevenueOnly: boolean} {
	const isPSAFullFinancials = hasModule(MODULE_TYPES.SAGE_PSA_FULL_FINANCIALS);
	const isPSARevenueOnly = hasModule(MODULE_TYPES.SAGE_PSA_REVENUE_ONLY);
	return {isPSAFullFinancials, isPSARevenueOnly};
}

export function sageFinancialsRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && !isPSARevenueOnly;
}

export function sageCostRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && isPSARevenueOnly;
}

export function sageProfitRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && isPSARevenueOnly;
}

export function sageMarginRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && isPSARevenueOnly;
}

export function sageRevenueRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && !isPSARevenueOnly;
}

export function sageProjectBudgetMoneyRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && !isPSARevenueOnly;
}

export function sageProjectRevenueRecognitionRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && isPSARevenueOnly;
}

export function sageProjectTaskBreakdownRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && !isPSARevenueOnly;
}

export function sageCreateExpenseRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && isPSARevenueOnly;
}

export function sageCreateInvoiceRestricted(): boolean {
	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED) && !isPSARevenueOnly && !isPSAFullFinancials;
}

export function sageReportingRestricted(): boolean {
	const sagePSARestricted = hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED);
	const sagePSAReportsMenuAccess = hasModule(MODULE_TYPES.SAGE_PSA_REPORTS_MENU_ACCESS);

	return sagePSARestricted && !sagePSAReportsMenuAccess;
}

export function sageAdminFinanceExpenseCategoriesRestricted(): boolean {
	const sagePSARestricted = hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED);
	const {isPSAFullFinancials} = sageRestrictedFinancialModules();

	return sagePSARestricted && !isPSAFullFinancials;
}

export function sageAdminFinanceRateCardRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && !isPSARevenueOnly;
}

export function sageAdminFinanceCurrenciesRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && !isPSARevenueOnly;
}

export function sageAdminFinanceBudgetToleranceRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && isPSARevenueOnly;
}

export function sageAdminFinanceInvoicingRestricted(): boolean {
	if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
		return false;
	}

	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();
	return !isPSAFullFinancials && isPSARevenueOnly;
}

export function sageFinanceMenuRestricted(): boolean {
	return (
		hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED) &&
		(sageFinancialsRestricted() || !hasModule(MODULE_TYPES.SAGE_PSA_FINANCE_MENU_ACCESS))
	);
}

export function isSagePSAWithFinancialsAccess(): boolean {
	const {isPSAFullFinancials, isPSARevenueOnly} = sageRestrictedFinancialModules();

	return hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED) && (isPSARevenueOnly || isPSAFullFinancials);
}
